/**
 * Role Configuration
 * Defines all roles and their hierarchy in the system
 */

import { logger } from '../services/logger';

// Role hierarchy - higher number = more permissions
export const ROLE_HIERARCHY = {
  // Super Admin and Admin roles
  'condo_super_admin': 100,
  'super_admin': 100,
  'condo_admin': 90,
  'admin': 90,

  // Manager roles
  'condo_manager': 70,
  'supervisor': 80,
  'sindico': 70,
  'administrador': 65, // Lower than sindico - only sees Monitoramento in Acesso menu

  // Staff roles
  'condo_staff': 60,
  'porteiro': 60,
  'zelador': 50,

  // Resident roles
  'condo_owner': 40,
  'proprietario': 40,
  'condo_resident': 30,
  'morador': 30,

  // Guest roles
  'condo_guest': 10,
  'visitante': 10,
} as const;

export type RoleName = keyof typeof ROLE_HIERARCHY;

// Role categories
export enum RoleCategory {
  ADMIN = 'admin',
  MANAGER = 'manager',
  STAFF = 'staff',
  RESIDENT = 'resident',
  GUEST = 'guest',
}

// Map roles to categories
export const ROLE_CATEGORIES: Record<RoleName, RoleCategory> = {
  'condo_super_admin': RoleCategory.ADMIN,
  'super_admin': RoleCategory.ADMIN,
  'condo_admin': RoleCategory.ADMIN,
  'admin': RoleCategory.ADMIN,

  'condo_manager': RoleCategory.MANAGER,
  'supervisor': RoleCategory.MANAGER,
  'sindico': RoleCategory.MANAGER,

  'condo_staff': RoleCategory.STAFF,
  'porteiro': RoleCategory.STAFF,
  'zelador': RoleCategory.STAFF,
  'administrador': RoleCategory.STAFF, // Administrador sees only Monitoramento, no Operational mode

  'condo_owner': RoleCategory.RESIDENT,
  'proprietario': RoleCategory.RESIDENT,
  'condo_resident': RoleCategory.RESIDENT,
  'morador': RoleCategory.RESIDENT,

  'condo_guest': RoleCategory.GUEST,
  'visitante': RoleCategory.GUEST,
};

/**
 * Check if user has a specific role
 */
export function hasRole(userRoles: string[], role: RoleName): boolean {
  return userRoles.includes(role);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(userRoles: string[], roles: RoleName[]): boolean {
  return roles.some(role => userRoles.includes(role));
}

/**
 * Check if user has ALL of the specified roles
 */
export function hasAllRoles(userRoles: string[], roles: RoleName[]): boolean {
  return roles.every(role => userRoles.includes(role));
}

/**
 * Get the highest role level the user has
 */
export function getHighestRoleLevel(userRoles: string[]): number {
  const levels = userRoles
    .filter(role => role in ROLE_HIERARCHY)
    .map(role => ROLE_HIERARCHY[role as RoleName]);

  return levels.length > 0 ? Math.max(...levels) : 0;
}

/**
 * Check if user has minimum role level
 */
export function hasMinimumRoleLevel(userRoles: string[], minimumLevel: number): boolean {
  return getHighestRoleLevel(userRoles) >= minimumLevel;
}

/**
 * Get user's primary role category
 */
export function getUserRoleCategory(userRoles: string[]): RoleCategory | null {
  logger.debug('roles.config', 'Getting user role category', {
    userRoles,
    userRolesJson: JSON.stringify(userRoles),
    userRolesLength: userRoles?.length
  });

  // Return null if no roles (which will default to OWNER in navigation)
  if (!userRoles || userRoles.length === 0) {
    logger.warn('roles.config', 'No roles found for user, returning null');
    return null;
  }

  // Check each role against ROLE_HIERARCHY
  logger.debug('roles.config', 'Checking roles against ROLE_HIERARCHY', {
    roleDetails: userRoles.map(role => ({
      role,
      level: ROLE_HIERARCHY[role as RoleName],
      category: ROLE_CATEGORIES[role as RoleName]
    }))
  });

  const highestLevel = getHighestRoleLevel(userRoles);
  logger.debug('roles.config', 'Highest level found', { highestLevel });

  // If no valid role level found, return null
  if (highestLevel === 0) {
    logger.warn('roles.config', 'No valid role level found, returning null');
    return null;
  }

  const highestRole = Object.entries(ROLE_HIERARCHY).find(
    ([role, level]) => level === highestLevel && userRoles.includes(role)
  );

  logger.debug('roles.config', 'Highest role found', { highestRole });

  if (highestRole) {
    const category = ROLE_CATEGORIES[highestRole[0] as RoleName];
    logger.info('roles.config', 'Role category determined', {
      category,
      role: highestRole[0]
    });
    return category;
  }

  logger.warn('roles.config', 'No matching role category found');
  return null;
}

/**
 * Check if user is admin (any admin role)
 */
export function isAdmin(userRoles: string[]): boolean {
  const adminRoles: RoleName[] = ['condo_super_admin', 'super_admin', 'condo_admin', 'admin'];
  return hasAnyRole(userRoles, adminRoles);
}

/**
 * Check if user is manager (manager or sindico - NOT administrador)
 */
export function isManager(userRoles: string[]): boolean {
  const managerRoles: RoleName[] = ['condo_manager', 'supervisor', 'sindico'];
  return hasAnyRole(userRoles, managerRoles);
}

/**
 * Check if user is staff (includes administrador - they see only Monitoramento, no Operational mode)
 */
export function isStaff(userRoles: string[]): boolean {
  const staffRoles: RoleName[] = ['condo_staff', 'porteiro', 'zelador', 'administrador'];
  return hasAnyRole(userRoles, staffRoles);
}

/**
 * Check if user is resident (owner or morador)
 */
export function isResident(userRoles: string[]): boolean {
  const residentRoles: RoleName[] = ['condo_owner', 'proprietario', 'condo_resident', 'morador'];
  return hasAnyRole(userRoles, residentRoles);
}

/**
 * Check if user is owner/proprietario
 */
export function isOwner(userRoles: string[]): boolean {
  const ownerRoles: RoleName[] = ['condo_owner', 'proprietario'];
  return hasAnyRole(userRoles, ownerRoles);
}

/**
 * Check if user is super admin
 */
export function isSuperAdmin(userRoles: string[]): boolean {
  const superAdminRoles: RoleName[] = ['condo_super_admin', 'super_admin'];
  return hasAnyRole(userRoles, superAdminRoles);
}

/**
 * Get user's display role (highest role)
 */
export function getDisplayRole(userRoles: string[]): string {
  const highestLevel = getHighestRoleLevel(userRoles);
  const highestRole = Object.entries(ROLE_HIERARCHY).find(
    ([role, level]) => level === highestLevel && userRoles.includes(role)
  );

  if (highestRole) {
    return formatRoleName(highestRole[0]);
  }

  return 'Usuário';
}

/**
 * Format role name for display
 */
export function formatRoleName(role: string): string {
  const roleNames: Record<string, string> = {
    'condo_super_admin': 'Super Administrador',
    'super_admin': 'Super Administrador',
    'condo_admin': 'Administrador',
    'admin': 'Administrador',
    'condo_manager': 'Gestor',
    'supervisor': 'Supervisor',
    'sindico': 'Síndico',
    'administrador': 'Administrador',
    'condo_staff': 'Funcionário',
    'porteiro': 'Porteiro',
    'zelador': 'Zelador',
    'condo_owner': 'Proprietário',
    'proprietario': 'Proprietário',
    'condo_resident': 'Morador',
    'morador': 'Morador',
    'condo_guest': 'Visitante',
    'visitante': 'Visitante',
  };

  return roleNames[role] || role;
}
